/**
 * Server-side 400 detail mapping for create-organization failures.
 * Mirrors the response shape produced by `org-plugin.routes.ts` and
 * `OrganizationService.create` when the derived plugin selection is invalid.
 *
 * Plugin selection itself is derived server-side from the onboarding profile
 * (see the server's `computeBootstrapSetForProfile`); the client never computes
 * a plugin list, so the only thing left here is surfacing a rejection when the
 * derived selection is somehow invalid — a Reflex-side config issue.
 */
export interface PluginBootstrapError {
  unknownPlugins?: string[];
  systemPluginsRequested?: string[];
  missingDependencies?: string[];
  /**
   * Plugins the org explicitly requested at bootstrap that are gated by
   * `requiredCapabilities` the org has not been granted. Surfaces a
   * super-admin actionable error: only a platform admin can grant the
   * capability.
   */
  capabilityGatedRequested?: string[];
  /**
   * Pre-release plugins requested at bootstrap. A new org has no release
   * channels enabled yet, so these must be installed from Admin → Plugins
   * after a manager enables the matching channel.
   */
  releaseChannelGatedRequested?: string[];
}

/**
 * Extract the structured plugin-rejection fields from a server response
 * body. Returns `undefined` when none are present so callers can fall
 * back to a generic toast. Used by both the create-organization dialog
 * (`ApiError.body`) and the setup wizard (`SetupApiError.body`).
 */
export function extractPluginBootstrapErrorDetail(
  body: Record<string, unknown> | undefined,
): PluginBootstrapError | undefined {
  if (!body) return undefined;
  const detail: PluginBootstrapError = {};
  if (isStringArray(body.unknownPlugins)) detail.unknownPlugins = body.unknownPlugins;
  if (isStringArray(body.systemPluginsRequested)) {
    detail.systemPluginsRequested = body.systemPluginsRequested;
  }
  if (isStringArray(body.missingDependencies)) {
    detail.missingDependencies = body.missingDependencies;
  }
  if (isStringArray(body.capabilityGatedRequested)) {
    detail.capabilityGatedRequested = body.capabilityGatedRequested;
  }
  if (isStringArray(body.releaseChannelGatedRequested)) {
    detail.releaseChannelGatedRequested = body.releaseChannelGatedRequested;
  }
  if (
    !detail.unknownPlugins &&
    !detail.systemPluginsRequested &&
    !detail.missingDependencies &&
    !detail.capabilityGatedRequested &&
    !detail.releaseChannelGatedRequested
  ) {
    return undefined;
  }
  return detail;
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'string');
}
